import { Flex } from '@chakra-ui/react';
import { Flash } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/tooltip';
import { mediaGlassPillProps } from './mentionCardStyle';

interface MentionBoostedBadgeProps {
  /** Denser sizing for the compact card variant. */
  compact?: boolean;
}

/**
 * Identification-only overlay marking a mention as a live co-publicite. An
 * icon-only glass disc pinned to the media's top-right; the label sits on a
 * hover tooltip so the badge stays compact and never crowds the platform chip
 * on narrow cards.
 *
 * Purely decorative for assistive tech: the boosted state is announced through
 * the parent card's aria-label. A translucent white fill over a warm hairline
 * (never a shadow) keeps it legible over any thumbnail.
 */
export const MentionBoostedBadge: React.FC<MentionBoostedBadgeProps> = ({ compact = false }) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      content={t('mentions.boostedBadge.short', { defaultValue: 'Boosted' })}
      showArrow
      openDelay={200}
      portalled
      positioning={{ placement: 'top' }}
    >
      <Flex
        position="absolute"
        top={compact ? 2 : 2.5}
        right={compact ? 2 : 2.5}
        align="center"
        justify="center"
        boxSize={compact ? '22px' : '26px'}
        aria-hidden={true}
        data-testid="mention-boosted-badge"
        {...mediaGlassPillProps}
      >
        <Flash
          width={compact ? 11 : 13}
          height={compact ? 11 : 13}
          color="var(--chakra-colors-dark)"
          strokeWidth={2}
        />
      </Flex>
    </Tooltip>
  );
};

export default MentionBoostedBadge;
